import { Card, Tag, Typography } from "antd";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import {
  LISTING_FEATURE_KEYS,
  LISTING_FEATURE_LABEL_KEYS,
  LISTING_PLACEHOLDER_IMAGE,
  type PlanListingCard,
  type UnitListingCard,
} from "~/types/listing";
import { getRouteWithParams } from "~/utils";
import { formatListingRent } from "~/utils/formatListingRent";

const cardImageStyle: React.CSSProperties = {
  height: 200,
  objectFit: "cover",
  width: "100%",
  display: "block",
  background: "#f0f2f5",
};

function formatListingLocation(card: {
  street: string;
  city: string;
}): string {
  return [card.street, card.city].filter(Boolean).join(", ");
}

function FeatureChips({
  features,
}: {
  features: PlanListingCard["features"] | UnitListingCard["features"];
}) {
  const { t } = useAppTranslation();
  const active = LISTING_FEATURE_KEYS.filter((key) => features[key] === true);

  if (active.length === 0) return null;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
      {active.slice(0, 4).map((key) => (
        <Tag key={key}>{t(LISTING_FEATURE_LABEL_KEYS[key])}</Tag>
      ))}
      {active.length > 4 ? (
        <Tag>{`+${active.length - 4}`}</Tag>
      ) : null}
    </div>
  );
}

function ListingCardShell({
  coverImageUrl,
  title,
  subtitle,
  priceLine,
  metaLine,
  features,
  onClick,
}: {
  coverImageUrl: string | null;
  title: string;
  subtitle: string;
  priceLine: string;
  metaLine: string;
  features: PlanListingCard["features"] | UnitListingCard["features"];
  onClick: () => void;
}) {
  return (
    <Card
      hoverable
      cover={
        <img
          alt={title}
          src={coverImageUrl ?? LISTING_PLACEHOLDER_IMAGE}
          style={cardImageStyle}
          onError={(event) => {
            event.currentTarget.src = LISTING_PLACEHOLDER_IMAGE;
          }}
        />
      }
      onClick={onClick}
      styles={{ body: { padding: 16 } }}
    >
      <Typography.Title level={5} style={{ margin: 0 }}>
        {title}
      </Typography.Title>
      <Typography.Text type="secondary">{subtitle}</Typography.Text>
      <Typography.Title level={4} style={{ margin: "8px 0 0" }}>
        {priceLine}
      </Typography.Title>
      <Typography.Text>{metaLine}</Typography.Text>
      <FeatureChips features={features} />
    </Card>
  );
}

export function PlanListingCardView({ card }: { card: PlanListingCard }) {
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  const priceLine =
    card.rentMin === card.rentMax
      ? formatListingRent(card.rentMin)
      : t("Listing_price_from", { amount: formatListingRent(card.rentMin) });

  const metaLine = t("Listing_plan_meta", {
    count: card.availableCount,
    beds: card.numBedroom,
    baths: card.numBathroom,
  });

  return (
    <ListingCardShell
      coverImageUrl={card.coverImageUrl}
      title={card.name}
      subtitle={formatListingLocation(card)}
      priceLine={priceLine}
      metaLine={metaLine}
      features={card.features}
      onClick={() =>
        navigate(getRouteWithParams("unitPlanDetails", { planID: card.id }))
      }
    />
  );
}

export function UnitListingCardView({ card }: { card: UnitListingCard }) {
  const navigate = useNavigate();
  const { t } = useAppTranslation();

  const metaParts = [
    t("Listing_unit_meta_beds_baths", {
      beds: card.numBedroom,
      baths: card.numBathroom,
    }),
  ];
  if (card.size) {
    metaParts.push(t("Listing_unit_meta_size", { size: card.size }));
  }

  return (
    <ListingCardShell
      coverImageUrl={card.coverImageUrl}
      title={card.name}
      subtitle={formatListingLocation(card)}
      priceLine={formatListingRent(card.rent)}
      metaLine={metaParts.join(" · ")}
      features={card.features}
      onClick={() =>
        navigate(getRouteWithParams("unitDetails", { unitID: card.id }))
      }
    />
  );
}
